'use client';

import React from 'react';
import { motion } from 'framer-motion';
import Container from '@/components/ui/layout/container';
import { flattenUrls, URLS } from '@/lib/consts';
import { formatForLabel } from '@/lib/utils';
import { Button } from '../button';
import Image from '../Image/image';
import { MotionLink } from '../Motion';
import type { NavigationItem } from '../NavBar/consts';
import {
  TypographyH3,
  TypographySmall,
} from '../typography';
import { SUPPORTED_CITIES } from '@/app/[city]/services/[category]/[type]/types';
import { SERVICE_AREA_DATA_MAP } from '@/app/about/our-service-areas/data';
import { footerMotionProperties } from './animations';
import { CONTACTS, FOOTER_SECTION_ID, SOCIAL_MEDIA } from './consts';

const LOGO_URI = 'cornerstone-homes-logo_amrijn';

const urls = flattenUrls<NavigationItem>(
  URLS,
  ['home', 'root'],
  (key, value) => {
    return {
      label: formatForLabel(key),
      href: value,
    };
  },
);

function Footer() {
  return (
    <motion.footer
      id={FOOTER_SECTION_ID}
      className="bg-primary text-primary-foreground"
      {...footerMotionProperties}
    >
      <Container id={FOOTER_SECTION_ID} maxWidth="max-w-5xl">
        <div className="flex flex-col gap-10">
          {/* Top: 3-column layout */}
          <div className="flex flex-col md:flex-row gap-10">
            {/* Column 1: Logo + Company name */}
            <div className="flex flex-col items-center md:items-start gap-4 flex-1">
              <Button variant="ghost" asChild className="p-0 h-auto hover:bg-transparent">
                <MotionLink
                  className="w-20 h-20 relative block"
                  href={URLS.HOME}
                >
                  <Image
                    src={LOGO_URI}
                    alt="Cornerstone Homes Logo"
                    fill
                    sizes="80px"
                  />
                </MotionLink>
              </Button>

              <TypographyH3 className="font-prata text-primary-foreground">
                Cornerstone Homes
              </TypographyH3>

              <TypographySmall className="text-primary-foreground/70">
                Building dreams with integrity since 2002.
              </TypographySmall>
            </div>

            {/* Column 2: Quick Links */}
            <div className="flex flex-col gap-4 flex-1">
              <TypographyH3 className="font-prata text-primary-foreground text-lg">
                Quick Links
              </TypographyH3>
              <nav>
                <ul className="flex flex-col gap-2">
                  {urls.map(url => (
                    <li key={url.label}>
                      <MotionLink
                        href={url.href ?? ''}
                        className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                      >
                        {url.label}
                      </MotionLink>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Column 3: Service Areas */}
            <div className="flex flex-col gap-4 flex-1">
              <TypographyH3 className="font-prata text-primary-foreground text-lg">
                Service Areas
              </TypographyH3>
              <nav>
                <ul className="flex flex-col gap-2">
                  {SUPPORTED_CITIES.map((slug) => {
                    const cityData = SERVICE_AREA_DATA_MAP[slug];
                    if (!cityData) return null;
                    return (
                      <li key={slug}>
                        <MotionLink
                          href={`/about/our-service-areas/${slug}`}
                          className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                        >
                          {cityData.name}
                        </MotionLink>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>

            {/* Column 4: Contact + Hours */}
            <div className="flex flex-col gap-6 flex-1">
              {/* Contact */}
              <div className="flex flex-col gap-3">
                <TypographyH3 className="font-prata text-primary-foreground text-lg">
                  Contact
                </TypographyH3>
                <div className="flex flex-col gap-1">
                  <TypographySmall className="text-primary-foreground/80">
                    16380 E 430 Rd.
                  </TypographySmall>
                  <TypographySmall className="text-primary-foreground/80">
                    Claremore OK, 74017
                  </TypographySmall>
                  {CONTACTS.map(contact => (
                    <TypographySmall
                      key={contact.name}
                      className="text-primary-foreground/80"
                    >
                      <a
                        href={`tel:${contact.phone}`}
                        className="hover:text-primary-foreground transition-colors"
                      >
                        {contact.name}: {contact.phone}
                      </a>
                    </TypographySmall>
                  ))}
                </div>
              </div>

              {/* Hours */}
              <div className="flex flex-col gap-3">
                <TypographyH3 className="font-prata text-primary-foreground text-lg">
                  Hours
                </TypographyH3>
                <div className="flex gap-8">
                  <div className="flex flex-col gap-1">
                    <TypographySmall className="text-primary-foreground/80 font-semibold">
                      Mon – Fri
                    </TypographySmall>
                    <TypographySmall className="text-primary-foreground/80">
                      9:00 am – 5:00 pm
                    </TypographySmall>
                  </div>
                  <div className="flex flex-col gap-1">
                    <TypographySmall className="text-primary-foreground/80 font-semibold">
                      Saturday
                    </TypographySmall>
                    <TypographySmall className="text-primary-foreground/80">
                      By appointment
                    </TypographySmall>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-primary-foreground/20" />

          {/* Bottom: Social + Attribution */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Social icons */}
            <div className="flex gap-4">
              {SOCIAL_MEDIA.map(({ name, render, uri }) => (
                <a
                  key={name}
                  href={uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  className="text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                >
                  {render({ size: 22 })}
                </a>
              ))}
            </div>

            {/* Attribution */}
            <TypographySmall className="text-primary-foreground/60 text-xs">
              Custom Website Design & Hosting by{' '}
              <a
                href="https://www.kjweb.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primary-foreground transition-colors"
              >
                KJ Web.Dev
              </a>
            </TypographySmall>
          </div>
        </div>
      </Container>
    </motion.footer>
  );
}

export default Footer;
